import { TuiNotificationService } from '@taiga-ui/core';
import { inject, Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { PatientLinkDirective } from '@modules/patient/patient-link.directive';
import { PatientMergeService } from '@modules/patient/patient-merge/patient-merge.service';
import { Store } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { FilterOperator, PaginationQuery } from '@pixacare/shared';
import { PatientHttpService } from '@services/http/patient.http.service';
import { patientsActions } from '@shared/store/patients/patients.actions';
import { selectDepartmentId } from '@shared/store/router/router.selectors';
import { catchError, combineLatest, iif, map, Observable, of, startWith, switchMap, throwError } from 'rxjs';

@Injectable()
export class PatientEditService {
  private readonly store = inject(Store);
  private readonly patientMergeService = inject(PatientMergeService);
  private readonly router = inject(Router);
  private readonly patientHttpService = inject(PatientHttpService);
  private readonly translocoService = inject(TranslocoService);
  private readonly alertService = inject(TuiNotificationService);

  private readonly departmentId$ = this.store.select(selectDepartmentId);

  // Return `true` if the two patients are the same.
  static compare(oldPatient: Patient, newPatient: Patient): boolean {
    return (
      oldPatient?.firstName === newPatient?.firstName &&
      oldPatient?.lastName === newPatient?.lastName &&
      oldPatient?.birthName === newPatient?.birthName &&
      oldPatient?.birthDate?.toLocaleDateString() === newPatient?.birthDate?.toLocaleDateString()
    );
  }

  updatePatient(basePatient: Patient, patientEditForm: UntypedFormGroup): Observable<Error> {
    const patient = patientEditForm.getRawValue();

    return iif(
      () => !patient.businessIdentifier,
      this.findPatientById(patient.clientCode, patient.id),
      this.findByBusinessIdentifier(patient.clientCode, patient.businessIdentifier),
    ).pipe(
      switchMap((targetPatient) => {
        if (
          patient.businessIdentifier &&
          basePatient.businessIdentifier !== patient.businessIdentifier &&
          targetPatient &&
          targetPatient.id
        ) {
          this.mergePatient(basePatient, targetPatient);
        } else {
          this.editPatient(basePatient.id, patient);
        }

        return of(null);
      }),
      catchError((e) => {
        this.handleError(e.statusText, e);
        return of(e);
      }),
    );
  }

  private handleError(message: string, error: Error): void {
    console.error('Patient Edit Service error', error);
    this.alertService
      .open(message, {
        label: this.translocoService.translate('Echec de la mise à jour du patient'),
        appearance: 'negative',
      })
      .subscribe();
  }

  private findPatientById(clientCode: string, patientId: number): Observable<Patient> {
    if (!patientId) {
      return throwError(() => new Error('Patient ID is required'));
    }

    return this.patientHttpService
      .getPatients(clientCode, {
        query: new PaginationQuery({
          filter: [
            {
              prop: 'id',
              op: FilterOperator.EQUAL,
              val: patientId.toString(),
            },
          ],
        }),
      })
      .pipe(map((response) => response?.data[0]));
  }

  private findByBusinessIdentifier(clientCode: string, businessIdentifier: string): Observable<Patient> {
    return this.patientHttpService.search({ clientCode, businessIdentifier }).pipe(map(([patient]) => patient));
  }

  private mergePatient(basePatient: Patient, targetPatient: Patient): void {
    combineLatest([
      this.patientMergeService.mergePatient(basePatient, targetPatient, {
        additionalMessage: this.translocoService.translate('Un patient possédant le même identifiant existe déjà.'),
      }),
      this.departmentId$.pipe(startWith(null)),
    ]).subscribe(([merged, departmentId]) => {
      if (merged) {
        const url = PatientLinkDirective.getRoute(targetPatient.clientCode, targetPatient.id, departmentId);
        this.router.navigate(url, { queryParamsHandling: 'merge' });
      }
    });
  }

  private editPatient(patientId: number, patient: Patient): void {
    this.store.dispatch(
      patientsActions.updatePatient({
        clientCode: patient.clientCode,
        patientId,
        patient: {
          ...patient,
          id: patientId,
          birthDate: new Date(patient.birthDate),
        },
      }),
    );
  }
}
